import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { AdminService } from './admin.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogs } from '../../shared/components/dialogs/confirmation-dialog/confirmation-dialogs.enum';
import { TenantIntegrationStatusType } from '../interfaces/tenants';

@Injectable({
	providedIn: 'root'
})
export class ExternalEventsHandlerService {

	public office365ConsentReceived = new Subject<any>();
	public MSTeamsMonitorConsentReceived = new Subject<any>();
	public ZappaPhoneConsentReceived = new Subject<any>();
	public urlProcessComplete = new BehaviorSubject<boolean>(false);

	constructor( private adminService: AdminService,
		public dialog: MatDialog ) 
	{ 
		// can not use HostListerner in a service
		window.addEventListener( 'message', (event) => {
			this.handleExternalMessages(event);
		} );
	}

	// there are three types of url handled here
	// office 365 admin consent.
	// MS Team user Monitor consent
	// Zappa Phone consent
	// both have different information. It is the information that will indicate the integration it is for
	async handleQueryParameters( url: string, params?: any ): Promise<void>
	{
		console.log("handleQueryParameters", url, params)
		let handled = false;
		try 
		{
			handled = await this.handleOffice365Consent(url, params);
		}
		catch (err)
		{
			console.log("Error handling office 365 consent", err);
			// assuming it was not handled
		}

		if ( !handled )
		{
			try
			{
				handled = await this.handleMSTeamsMonitorConsent(url, params);
			}
			catch (err)
			{
				console.log("Error handling user monitor consent", err);
				// assuming it was not handled
			}
		}

		if ( !handled )
		{
			try
			{
				handled = await this.handleZappaPhoneConsent(url, params);
			}
			catch (err)
			{
				console.log("Error handling zappa phone consent", err);
				// assuming it was not handled
			}
		}

		if ( !handled )
		{
			this.urlProcessComplete.next(true);
			// done
		}
		return;
	}

	handleOffice365Consent( url: string, params?: any ): Promise<any>
	{
		let context = undefined;

		console.log("Here are the params the app was invoked with", params)
		let accountId = undefined;
		let stateDecoded = undefined;

		// special handling when the office 365 admin cancels consent.
		// it appears the return url contains both an error and consent == true.
		// if the url contains error_uri, split the url and only use the part without the error_uri

		let parts = url.split('error_uri');
		let useUrl = parts[0];
		console.log("Use this url", useUrl)
		if ( params && params.state )
		{
			console.log("the state before decoding", params.state)
			// these states from this process were enncoded twice
			let decoded = decodeURIComponent(decodeURIComponent(params.state));
			console.log("the state after decoding", decoded)
			// we have had issues in the past where office 365 is sending invalid chars so it can not parse properly
			// lets remove the ones we know about if they exist
			decoded = decoded.replace( /\r\n/g, " " );

			// the try is only there so we can log if we can find any other cases of the data passed in the url 
			// being invalid for JSON
			try{
				stateDecoded = JSON.parse(decoded);
				console.log("the state after parsing", stateDecoded)
			}
			/* istanbul ignore next */ 
			catch(error)
			{
				console.log("Error during parsing", error, decoded);
			}

			if ( stateDecoded.accountId )
			{
				accountId = stateDecoded.accountId;
			}
			
			if ( stateDecoded.context )
			{
				context = stateDecoded.context;
			}
		
			if ( accountId == undefined )
			{
				return Promise.resolve(false); // nothing to process here
			}
				
			// for Office365 integration we are looking to see if the consent succeeded
			if ( (useUrl.indexOf("admin_consent=True") != -1) || (stateDecoded && stateDecoded.admin_consent == true) )
			{
				let message = {
					type: "CL_OFFICE365_CONSENT",
					granted: true,
					accountId: accountId
				}
				console.log("The ADMIN CONSENT is GRANTED")
				//save the status in the accounts tags
				return this.saveOffice365StateForAccount(accountId, true).then( data => {
					if ( context == "Office365Consent" ) // this means it was a direct url
					{
						this.urlProcessComplete.next(true);
						const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.OFFICE365_CONSENT_SUCCESS } });
						return dialogRef.afterClosed().toPromise().then(result => {
							return true;
						});
					}
					else
					{
						if ( window.opener )
							window.opener.postMessage( message, '*' );
						window.close();
					}
				})
			}
			// for Office365 integration we are looking to see if the consent failed and possible reason
			// error=access_denied&error_subcode=cancel
			else if ( (useUrl.indexOf("error=access_denied&error_subcode=cancel") != -1) ||  (stateDecoded && stateDecoded.error == "access_denied") )
			{
				let message = {
					type: "CL_OFFICE365_CONSENT",
					granted: false,
					error: "access_denied",
					accountId: accountId
				}
				console.log("THE ADMIN CONSENT FAILED")
				return this.saveOffice365StateForAccount(accountId, false).then(data => {
					if ( context == "Office365Consent" )
					{
						this.urlProcessComplete.next(true);
						const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.OFFICE365_CONSENT_FAILURE } });
						return dialogRef.afterClosed().toPromise().then(result => {
							return true;
						});
					}
					else
					{
						if ( window.opener )
							window.opener.postMessage( message, '*' );
						window.close();
					}
				});
			}
			// for Office365 integration we are looking to see if the consent failed and possible reason
			// error=access_denied&error_description= some error message
			else if ( (useUrl.indexOf("error=access_denied&error_description=") != -1) ||  (stateDecoded && stateDecoded.error == "access_denied") )
			{
				let parts = useUrl.split("error=access_denied&error_description=");

				let errorDescription = '';
				if ( parts && parts.length > 1 )
				{
					errorDescription = decodeURIComponent( parts[1] );
					errorDescription = errorDescription.replace(/\+/g, " ");
				}
					
				let message = {
					type: "CL_OFFICE365_CONSENT",
					granted: false,
					error: "access_denied",
					accountId: accountId,
					description: errorDescription
				}
				console.log("THE ADMIN CONSENT FAILED")
				return this.saveOffice365StateForAccount(accountId, false).then(data => {
					if ( context == "Office365Consent" )
					{
						this.urlProcessComplete.next(true);
						const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.OFFICE365_CONSENT_FAILURE } });
						return dialogRef.afterClosed().toPromise().then(result => {
							return true;
						});
					}
					else
					{
						console.log("O365 consent to close window")
						if ( window.opener )
							window.opener.postMessage( message, '*' );
						window.close();
					}
				});
			}
		}
		else
		{
			return Promise.resolve(false); // nothing to process here
		}
	}

	handleMSTeamsMonitorConsent( url: string, params?: any ): Promise<any>
	{
		let connected = undefined;
		let error = undefined;
		let integrationId = undefined;
		let connectionsId = undefined;

		console.log("Here are the params the app was invoked with", params)
		let stateDecoded = undefined;

		if ( params && params.state ) // if we have a state, this was the result of a copy URL
		{
			console.log("the state before decoding", params.state)
			// these states from this process were enncoded twice
			let decoded = decodeURIComponent(decodeURIComponent(params.state));
			console.log("the state after decoding", decoded)
			// we have had issues in the past where office 365 is sending invalid chars so it can not parse properly
			// lets remove the ones we know about if they exist
			decoded = decoded.replace( /\r\n/g, " " );

			// the try is only there so we can log if we can find any other cases of the data passed in the url 
			// being invalid for JSON
			try{
				stateDecoded = JSON.parse(decoded);
				console.log("the state after parsing", stateDecoded)
			}
			/* istanbul ignore next */ 
			catch(error)
			{
				console.log("Error during parsing", error, decoded);
			}

			if ( stateDecoded.integrationid )
			{
				integrationId = stateDecoded.integrationid;
			}
		
			if ( stateDecoded.connectionid )
			{
				connectionsId = stateDecoded.connectionid;
			}

			if ( integrationId == undefined && connectionsId == undefined )
			{
				return Promise.resolve(false); // nothing to process here
			}
				
			if ( stateDecoded.connected ) /// do we need this?
			{
				connected = stateDecoded.connected;
			}
			
			if ( stateDecoded.error )
			{
				error = stateDecoded.error;
			}

			// consent succeeded
			if ( error == undefined )
			{
				this.urlProcessComplete.next(true);
				const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.MSTEAMS_MONITOR_CONSENT_SUCCESS } });
				return dialogRef.afterClosed().toPromise().then(result => {
					return true;
				});
			}
			else
			{
				// can we be more specific with this error?
				this.urlProcessComplete.next(true);
				const dialogRef = this.dialog.open(ConfirmationDialogComponent, { data: { mode: ConfirmationDialogs.MSTEAMS_MONITOR_CONSENT_FAILURE } });
				return dialogRef.afterClosed().toPromise().then(result => {
					return true;
				});
			}
		}
		else if ( params && params.integrationid && params.integrationid == "microsoft" )
		{
			// scenario where the account admin is doing the consent
			console.log("the url ", url);
			let message = {
				type: "CL_MONITORUSER_CONSENT",
				granted: params.connected,
				connectionId: params.connectionid,
				error: params.error,
				url: url // to be removed once we understand the url contents
			}
			console.log("The MONITOR CONSENT HAS BEEN RECEIVED")
			
			if ( window.opener )
				window.opener.postMessage( message, '*' );

			window.close();

		}
		else{
			return Promise.resolve(false); // nothing to process here
		}
	}

	handleZappaPhoneConsent( url: string, params?: any ): Promise<any>
	{
		console.log("Zappa consent: the url the app was invoked with", url)
		console.log("Zappa consent: the params the app was invoked with", params)
		let message = undefined;
		if ( url.indexOf('integrationId=zappaphone') != -1 )
		{
			// scenario where the account admin is doing the consent
			let error = undefined;
			try 
			{
				// in case there is a problem decoding, we will wrap it in a try/ catch
				if (params?.error)
				{
					error = decodeURIComponent(params.error);
					error = error.replace(/\+/g, " "); // seeing a lot of +
				}
			}
			catch (err)
			{
				console.log("Can not decode the error from Zoom auth, use error as is", err);
				error = params?.error;
			}

			message = {
				type: "CL_ZAPPAPHONE_CONSENT",
				tenantId: params?.tenantId,
				connected: params?.integrationStatus == TenantIntegrationStatusType.CONNECTED,
				error: error,
				url: url // to be removed once we understand the url contents
			}
			console.log("The ZAPPA PHONE CONSENT HAS BEEN RECEIVED, sending message", message)

			if ( window.opener )
				window.opener.postMessage( message, '*' );

			window.close();
		}
		else{
			return Promise.resolve(false); // nothing to process here
		}
	}

	handleExternalMessages(event: any)
	{
		// for Office365 integration to determine if the parent accounts app is receiving 
		// messages from the popped child app
		if ( event && event.data && event.data.type === "CL_OFFICE365_CONSENT" ){
			console.log("PARENT WINDOW WAS NOTIFIED OF OFFICE365 CONSENT", event)
			this.office365ConsentReceived.next(event.data);
		}

		if ( event && event.data && event.data.type === "CL_MONITORUSER_CONSENT" ){
			console.log("PARENT WINDOW WAS NOTIFIED OF MONITORUSER CONSENT", event)
			this.MSTeamsMonitorConsentReceived.next(event.data);
		}

		if ( event && event.data && event.data.type === "CL_ZAPPAPHONE_CONSENT" ){
			console.log("PARENT WINDOW WAS NOTIFIED OF ZAPPA PHONE CONSENT", event)
			this.ZappaPhoneConsentReceived.next(event.data);
		}
	}

	saveOffice365StateForAccount( accountId: string, consent: boolean )
	{
		let currentTime = undefined;
		if ( consent == true )
		{
			let d = new Date();
			currentTime = d.getTime();
		}
		return this.adminService.getAccountTags( accountId ).then( tags => {
			// there should be tags -- there would not be an integration without tags
			let enabled = false;
			if ( tags && tags.Office365 )
				enabled = tags.Office365.enabled; //preseve the state of enabled

			let dataChanges = {
				tags: {
					Office365: {
						enabled: enabled,
						consent: consent,
						verifiedDate: currentTime
					}
				}
			}
	
			return this.adminService.updateAccount(accountId, dataChanges);
		})

	}

}
